import React from 'react';
import { KoobTitle } from '@koob/ui';
import { getFolderBaseStatus, getFolderStatus } from '@koob/utils';
import FolderStatusBadge from './FolderStatusBadge';
import type { FolderActionFolder } from './types/folderActions';

export interface FolderHeaderProps {
  folder: FolderActionFolder | null;
  /** Shown while the folder loads. */
  fallbackTitle?: string;
  backButton?: React.ReactNode;
  /** Shown after the status badge (e.g. the trip language). */
  titleAddon?: React.ReactNode;
  /** The app's own actions, laid out on the right. */
  children?: React.ReactNode;
}

/**
 * KD-3842: "NAME - REF" and the status badge (with the previous status once
 * archived). Each app passes the actions it offers as children.
 */
export default function FolderHeader({
  folder,
  fallbackTitle,
  backButton,
  titleAddon,
  children,
}: FolderHeaderProps) {
  const archived = Boolean(folder?.archivedAt);

  const title = folder
    ? [folder.name, folder.icr].filter(Boolean).join(' - ')
    : fallbackTitle;

  // Wide screens keep the actions on one line, in place: the title wraps
  // instead, cut after three lines (full title on hover) (KD-3910). It keeps
  // a minimum width, and the badges go under it when it needs the room.
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 lg:flex-nowrap">
      <div className="flex min-w-0 flex-1 items-center gap-4">
        {backButton && <span className="shrink-0">{backButton}</span>}
        <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
          <KoobTitle
            size="text-xl"
            className="line-clamp-3 min-w-[8rem] max-w-[420px] [overflow-wrap:anywhere]"
          >
            <span title={title}>{title}</span>
          </KoobTitle>
          {folder && (
            <span className="flex shrink-0 items-center gap-2.5">
              <FolderStatusBadge
                status={getFolderStatus(folder)}
                previousStatus={
                  archived ? getFolderBaseStatus(folder) : undefined
                }
              />
              {titleAddon}
            </span>
          )}
        </div>
      </div>

      {folder && children && (
        // ml-auto keeps the actions on the right even once they wrap.
        <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2 lg:flex-nowrap">
          {children}
        </div>
      )}
    </div>
  );
}
